import { SkillData } from "types/battle-engine/battle";
import { StrokedText } from "../ui/text/stroked-text";
import cn from "classnames";
import { isXsScreen } from "@/utils/screen-size-check";

// Helper function to convert hex color to rgba string
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Type color mapping (hex values from CSS)
const typeColors: Record<string, string> = {
  normal: "#a8a878",
  fire: "#f08030",
  water: "#6890f0",
  electric: "#f8d030",
  grass: "#78c850",
  ice: "#98d8d8",
  fighting: "#c03028",
  poison: "#a040a0",
  ground: "#e0c068",
  flying: "#a890f0",
  psychic: "#f85888",
  bug: "#a8b820",
  rock: "#b8a038",
  ghost: "#705898",
  dragon: "#7038f8",
  dark: "#705848",
  steel: "#b8b8d0",
  fairy: "#ee99ac",
};

const colorsPerType = {
  fire: {
    top: "from-pk-red-5",
    bottom: "via-pk-red-3/40 to-pk-red-4",
  },
  water: {
    top: "from-pk-blue-5",
    bottom: "via-pk-blue-3/40 to-pk-blue-4",
  },
  grass: {
    top: "from-pk-green-5",
    bottom: "via-pk-green-3/40 to-pk-green-4",
  },
  electric: {
    top: "from-pk-yellow-3",
    bottom: "via-pk-yellow-3/40 to-pk-yellow-2",
  },
  psychic: {
    top: "from-pk-garnet-5",
    bottom: "via-pk-garnet-3/40 to-pk-garnet-4",
  },
  poison: {
    top: "from-pk-purple-5",
    bottom: "via-pk-purple-3/40 to-pk-purple-4",
  },
  ground: {
    top: "from-pk-beige-3",
    bottom: "via-pk-beige-3/40 to-pk-beige-4",
  },
  flying: {
    top: "from-pk-gray-5",
    bottom: "via-pk-gray-3/40 to-pk-gray-4",
  },
  bug: {
    top: "from-pk-green-5",
    bottom: "via-pk-green-3/40 to-pk-green-4",
  },
  rock: {
    top: "from-pk-gray-3",
    bottom: "via-pk-gray-3/40 to-pk-gray-3",
  },
  ice: {
    top: "from-pk-blue-5",
    bottom: "via-pk-blue-3/40 to-pk-blue-4",
  },
  dragon: {
    top: "from-pk-red-5",
    bottom: "via-pk-red-3/40 to-pk-red-4",
  },
  fairy: {
    top: "from-pk-garnet-5",
    bottom: "via-pk-garnet-3/50 to-pk-garnet-4",
  },
  dark: {
    top: "from-pk-gray-5",
    bottom: "via-pk-gray-3/40 to-pk-gray-4",
  },
  steel: {
    top: "from-pk-gray-5",
    bottom: "via-pk-gray-3/40 to-pk-gray-4",
  },
  ghost: {
    top: "from-pk-gray-2",
    bottom: "via-pk-gray-3/40 to-pk-purple-2",
  },
  fighting: {
    top: "from-pk-red-4",
    bottom: "via-pk-gray-3/40 to-pk-red-4",
  },
  normal: {
    top: "from-pk-gray-5",
    bottom: "via-pk-gray-3/40 to-pk-gray-4",
  },
};

interface MiniSkillCardProps {
  skill: SkillData;
  className?: string;
}

export const MiniSkillCard = ({ skill, className }: MiniSkillCardProps) => {
  const isXs = isXsScreen();
  const colors =
    colorsPerType[
      skill.pokemon_type.toLocaleLowerCase() as keyof typeof colorsPerType
    ];

  const typeName = skill.pokemon_type.toLocaleLowerCase();
  const typeColor = typeColors[typeName];
  if (!typeColor) throw new Error(`Type color not found for ${typeName}`);
  const backgroundColor = hexToRgba(typeColor, 1);

  return (
    <div
      className={cn(
        "flex flex-col group overflow-hidden shadow-cartoon relative rounded-lg p-0 w-full h-full",
        isXs && "h-auto",
        className,
      )}
    >
      <div
        className={cn(
          "relative bg-cover bg-center h-full w-full flex flex-col gap-1 p-1 pt-4 z-[2] rounded-lg border-pk-gray-4/80 border-b-2 rounded-b-xl",
          isXs && "pt-1 gap-0.5 border-b-0",
        )}
        style={{
          backgroundImage: `url('/assets/card-art/${skill.id}.jpg')`,
        }}
      >
        <div
          className={`absolute top-0 left-0 w-full h-full bg-gradient-to-br ${colors.top} to-transparent z-[1] rounded-xl`}
        ></div>
        <div
          className={`absolute top-0 left-0 w-full h-full bg-gradient-to-tl from-transparent ${colors.bottom} z-[1] rounded-xl`}
        ></div>
        <div
          className="absolute top-0 left-0 w-full h-full opacity-10 z-[1] rounded-xl"
          style={{
            backgroundImage: "url('/assets/app-ui/textre.png')",
            backgroundSize: "200%",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        ></div>

        {isXs ? (
          <StrokedText
            fontSize={10}
            strokeWidth={4}
            height={10}
            className="ml-1 z-10"
          >
            {skill.name}
          </StrokedText>
        ) : (
          skill.name.split(" ").map((word, index) => (
            <StrokedText
              fontSize={14}
              strokeWidth={6}
              height={12}
              className="ml-1 z-10 "
              key={index}
            >
              {word}
            </StrokedText>
          ))
        )}

        <div
          className={cn(
            `flex flex-row gap-1 items-center mt-auto z-[12] rounded-full bg-pk-${typeName} px-1.5 py-0.5 w-max`,
            isXs && "px-1 py-0 mt-0",
          )}
          style={{
            backgroundColor,
          }}
        >
          <img
            src={`/assets/pokemon-types/${skill.pokemon_type.toLocaleLowerCase()}.svg`}
            alt={skill.pokemon_type}
            className={cn("w-3 h-3", isXs && "w-2 h-2")}
          />
          <span className={cn("text-xs", isXs && "text-[9px]")}>
            {skill.pokemon_type}
          </span>
        </div>
      </div>

      {!isXs && (
        <div className="w-full h-2 translate-y-1 rounded-b-lg z-[1] absolute bottom-0 left-0 bg-pk-gray-1"></div>
      )}
    </div>
  );
};
